// src/components/Login.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthWrapper from "./Auth";
import "./auth.css";

import { supabase } from "../supabaseClient";
import { TOTP, Secret } from "otpauth";

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // 2FA STATES
  const [require2FA, setRequire2FA] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // user email/pass to re-login after otp
  const [pendingUser, setPendingUser] = useState(null);

  // ------------------------------------------------
  // STEP 1 → NORMAL LOGIN
  // ------------------------------------------------
  const handleLogin = async () => {
    setLoading(true);
    setMsg("");

    // 1. try password login
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setMsg(error.message);
      setLoading(false);
      return;
    }

    const user = data.user;

    // 2. load settings from DB
    const { data: row } = await supabase
      .from("users_data")
      .select("settings")
      .eq("user_id", user.id)
      .single();

    const userSettings = row?.settings || {};

    // 3. if 2FA enabled
    if (userSettings.authEnabled && userSettings.authSecret) {
      // ❗ logout immediately → prevents bypass
      await supabase.auth.signOut();

      // store email/pass to re-login later
      setPendingUser({ email, password });

      // open OTP popup
      setTwoFASecret(userSettings.authSecret);
      setRequire2FA(true);
      setLoading(false);
      return;
    }

    // no 2FA → login success
    setLoading(false);
    navigate("/");
  };

  // ------------------------------------------------
  // STEP 2 → VERIFY 2FA
  // ------------------------------------------------
  const verify2FA = async () => {
    setVerifyLoading(true);
    setMsg("");

    try {
      const totp = new TOTP({
        issuer: "MyApp Secure",
        label: email,
        secret: Secret.fromBase32(twoFASecret),
      });

      const delta = totp.validate({ token: code });

      if (delta === null) {
        setMsg("Invalid Code");
        setVerifyLoading(false);
        return;
      }

      // OTP correct → re-login with stored email/pass
      const { error } = await supabase.auth.signInWithPassword({
        email: pendingUser.email,
        password: pendingUser.password,
      });

      if (error) {
        setMsg("Unexpected Login Error");
        setVerifyLoading(false);
        return;
      }

      setVerifyLoading(false);
      navigate("/");

    } catch (err) {
      setMsg("Verification failed");
      setVerifyLoading(false);
    }
  };

  return (
    <AuthWrapper>
      <div className="qx-card">
        <h2 className="qx-title">Log In</h2>

        <div className="qx-tab-container">
          <button className="qx-tab active">Login</button>
          <Link to="/signup" className="qx-tab">Registration</Link>
        </div>

        <input
          className="qx-input"
          type="email"
          placeholder="Email"
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          className="qx-input"
          type="password"
          placeholder="Password"
          onChange={(e) => setPassword(e.target.value)}
        />

        <button className="qx-btn" onClick={handleLogin} disabled={loading}>
          {loading ? <div className="spinner"></div> : "Sign In"}
        </button>

        {msg && !require2FA && <p className="auth-error">{msg}</p>}
      </div>

      {/* ===================================================
          AUTHENTICATOR POPUP — SAME UI, NO CHANGES IN LOOK
         =================================================== */}
      {require2FA && (
        <div className="qx-2fa-overlay">
          <div className="qx-2fa-box">

            <h3 className="qx-2fa-title">Enter 2FA Code</h3>

            <p className="qx-2fa-text">
              Open Google Authenticator and enter the 6-digit code.
            </p>

            <input
              type="text"
              maxLength={6}
              className="qx-2fa-input"
              placeholder="123456"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />

            <button
              className="qx-btn qx-2fa-btn"
              onClick={verify2FA}
              disabled={verifyLoading}
            >
              {verifyLoading ? <div className="spinner"></div> : "Verify Login"}
            </button>

            {msg && <p className="auth-error">{msg}</p>}
          </div>
        </div>
      )}
    </AuthWrapper>
  );
}
